/**
 * OP Readiness page (Stage 7a — Publishing Nexus UI, readiness + audit consumption).
 *
 * Read-only surface over the canonical platform (core -> OP projection -> audit -> dispatch):
 * readiness dashboard, product browser with payload/audit/dispatch detail, mismatch browser.
 * Dispatch CONTROLS are Stage 7b (gated with the live writer) and intentionally absent.
 */
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchOPReadinessSummary, isUnavailable } from '@/lib/op-readiness';
import { ProductsView } from '@/components/op-readiness/ProductsView';
import { MismatchBrowser } from '@/components/op-readiness/MismatchBrowser';
import { DispatchBrowser } from '@/components/op-readiness/DispatchBrowser';
import { DispatchControlPanel } from '@/components/op-readiness/DispatchControlPanel';
import { SeverityBadge, StatusBadge, UnavailableNotice, formatDateTime } from '@/components/op-readiness/shared';

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function OpReadinessPage() {
  const summaryQuery = useQuery({
    queryKey: ['op-readiness', 'summary'],
    queryFn: fetchOPReadinessSummary,
    refetchInterval: 30000,
  });

  const summary = summaryQuery.data;

  if (summaryQuery.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-7 w-48" />
        <div className="grid gap-3 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (summary && isUnavailable(summary)) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-xl font-semibold">OP Readiness</h1>
        <UnavailableNotice reason={summary.reason} />
      </div>
    );
  }

  const rollup = summary?.rollup ?? [];
  const totalPayloads = rollup.reduce((s, r) => s + r.Products, 0);
  const writable = rollup.filter((r) => r.CanWrite).reduce((s, r) => s + r.Products, 0);
  const blocked = totalPayloads - writable;
  const latestRun = summary?.auditRuns?.find((r) => r.Status === 'SUCCEEDED');

  return (
    <div className="space-y-5 p-6">
      <div>
        <h1 className="text-xl font-semibold">OP Readiness</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Canonical platform → OP: payload readiness, live-OP audit parity, and mismatch review
          (read-only; projection {summary?.projectionVersion}).
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard label="Payloads" value={totalPayloads.toLocaleString()} hint="mart.OPProductPayload" />
        <SummaryCard label="Writable" value={writable.toLocaleString()} hint="CanWrite = 1" />
        <SummaryCard label="Blocked" value={blocked.toLocaleString()} hint="mostly missing EAN" />
        <SummaryCard
          label="Latest audit parity"
          value={latestRun?.ParityPct != null ? `${latestRun.ParityPct}%` : '—'}
          hint={latestRun ? `${latestRun.TotalRecords} sampled · ${formatDateTime(latestRun.StartedAt)}` : 'no runs yet'}
        />
      </div>

      {summary?.topMismatchFields && summary.topMismatchFields.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <h2 className="mb-2 text-sm font-semibold">Top mismatch fields (latest soak)</h2>
          <div className="flex flex-wrap gap-2">
            {summary.topMismatchFields.slice(0, 8).map((f) => (
              <div key={f.FieldPath} className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                <span className="font-mono">{f.FieldPath}</span>
                <SeverityBadge severity={f.Severity} />
                <span className="tabular-nums text-muted-foreground">{f.MismatchCount}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="mismatches">Mismatches</TabsTrigger>
          <TabsTrigger value="runs">Audit runs</TabsTrigger>
          <TabsTrigger value="dispatch">Dispatch</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="pt-3">
          <ProductsView />
        </TabsContent>
        <TabsContent value="mismatches" className="pt-3">
          <MismatchBrowser />
        </TabsContent>
        <TabsContent value="runs" className="pt-3">
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">Started</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Sampled</th><th className="px-3 py-2">Full match</th>
                  <th className="px-3 py-2">Mismatch</th><th className="px-3 py-2">Not in OP</th>
                  <th className="px-3 py-2">Parity</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.auditRuns ?? []).map((r) => (
                  <tr key={r.AuditRunId} className="border-b last:border-0">
                    <td className="whitespace-nowrap px-3 py-2">{formatDateTime(r.StartedAt)}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.Status} /></td>
                    <td className="px-3 py-2 tabular-nums">{r.TotalRecords}</td>
                    <td className="px-3 py-2 tabular-nums">{r.ReadyCount}</td>
                    <td className="px-3 py-2 tabular-nums">{r.MismatchCount}</td>
                    <td className="px-3 py-2 tabular-nums">{r.BlockedCount}</td>
                    <td className="px-3 py-2 font-medium tabular-nums">{r.ParityPct != null ? `${r.ParityPct}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="dispatch" className="pt-3 space-y-4">
          <DispatchControlPanel />
          <DispatchBrowser />
        </TabsContent>
      </Tabs>
    </div>
  );
}
